const OPERATOR_EMAIL_KEY = 'beatq.admin.operatorEmail';
const ACCESS_REASON_KEY = 'beatq.admin.accessReason';
const BANNER_KEY = 'beatq.admin.impersonationBanner';
const ENDED_GUARD_KEY = 'beatq.admin.impersonationEndedGuard';
const VISITED_DURING_KEY = 'beatq.admin.impersonationVisited';

export type ImpersonationIntensity = 'tenant-view' | 'impersonate-read' | 'impersonate-write';

export type ImpersonationBannerState = {
  intensity: ImpersonationIntensity;
  sessionId?: string;
  subjectName: string;
  subjectId: string;
  caseId?: string;
  caseHref?: string;
  expiresAt?: string;
  consentAt?: string;
};

export type ImpersonationEndedGuard = {
  returnHref: string;
  blockedPaths: string[];
  endedAt: number;
  /** Set when the clock expired so chrome can announce the return. */
  notice?: 'expired';
};

function storageGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string | null): void {
  try {
    if (value == null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
}

function persistentGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function persistentSet(key: string, value: string | null): void {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
}

/** Persist signed-in email for header identity (JWT has no email claim). */
export function setStoredOperatorEmail(email: string | null): void {
  storageSet(OPERATOR_EMAIL_KEY, email?.trim() || null);
}

export function getStoredOperatorEmail(): string | null {
  return storageGet(OPERATOR_EMAIL_KEY)?.trim() || null;
}

/** Purpose-binding justification (X-Access-Reason) for subject opens without a case. */
export function setStoredAccessReason(reason: string | null): void {
  storageSet(ACCESS_REASON_KEY, reason?.trim() || null);
}

export function getStoredAccessReason(): string | null {
  return storageGet(ACCESS_REASON_KEY)?.trim() || null;
}

export function getImpersonationBanner(): ImpersonationBannerState | null {
  const raw = persistentGet(BANNER_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ImpersonationBannerState;
    if (!parsed?.intensity || !parsed.subjectId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export const IMPERSONATION_CHANGED_EVENT = 'beatq-admin-impersonation';

function emitImpersonationChanged(): void {
  try {
    window.dispatchEvent(new Event(IMPERSONATION_CHANGED_EVENT));
  } catch {
    /* jsdom / private mode */
  }
}

export function setImpersonationBanner(state: ImpersonationBannerState | null): void {
  persistentSet(BANNER_KEY, state ? JSON.stringify(state) : null);
  if (!state) storageSet(VISITED_DURING_KEY, null);
  emitImpersonationChanged();
}

/** Display clock from A04 consent ISO (architecture: "consent recorded 14:02"). */
export function formatConsentClock(iso?: string | null): string | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  try {
    return new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

export function hydrateImpersonationBannerFromSessionId(
  sessionId: string,
  extra?: Partial<ImpersonationBannerState>,
): ImpersonationBannerState {
  const existing = getImpersonationBanner();
  if (existing?.sessionId === sessionId) {
    const merged = { ...existing, ...extra, sessionId };
    setImpersonationBanner(merged);
    return merged;
  }
  const expiresAt = extra?.expiresAt ?? new Date(Date.now() + 15 * 60 * 1000).toISOString();
  const next: ImpersonationBannerState = {
    intensity: extra?.intensity ?? 'impersonate-read',
    sessionId,
    subjectName: extra?.subjectName ?? 'Subject',
    subjectId: extra?.subjectId ?? sessionId,
    caseId: extra?.caseId,
    caseHref: extra?.caseHref,
    expiresAt,
    consentAt: extra?.consentAt,
  };
  setImpersonationBanner(next);
  return next;
}

export function remainingSeconds(expiresAt: string | undefined, now = Date.now()): number {
  if (!expiresAt) return 0;
  const end = Date.parse(expiresAt);
  if (Number.isNaN(end)) return 0;
  return Math.max(0, Math.floor((end - now) / 1000));
}

export function formatCountdown(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function isTwoMinuteWarning(totalSeconds: number): boolean {
  return totalSeconds > 0 && totalSeconds <= 120;
}

export function recordImpersonationVisit(pathname: string): void {
  const raw = storageGet(VISITED_DURING_KEY);
  let paths: string[] = [];
  try {
    paths = raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    paths = [];
  }
  if (!paths.includes(pathname)) paths.push(pathname);
  storageSet(VISITED_DURING_KEY, JSON.stringify(paths));
}

export function getImpersonationVisitedPaths(): string[] {
  const raw = storageGet(VISITED_DURING_KEY);
  try {
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

const DEFAULT_BLOCKED = ['/impersonation', '/support-mirror'];

export function setImpersonationEndedGuard(guard: ImpersonationEndedGuard | null): void {
  storageSet(ENDED_GUARD_KEY, guard ? JSON.stringify(guard) : null);
}

export function getImpersonationEndedGuard(): ImpersonationEndedGuard | null {
  const raw = storageGet(ENDED_GUARD_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ImpersonationEndedGuard;
  } catch {
    return null;
  }
}

export function shouldBlockImpersonatedBack(
  pathname: string,
  guard: ImpersonationEndedGuard | null,
): boolean {
  if (!guard) return false;
  if (guard.blockedPaths.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
    return true;
  }
  return DEFAULT_BLOCKED.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function buildEndedGuard(
  returnHref: string,
  notice?: ImpersonationEndedGuard['notice'],
): ImpersonationEndedGuard {
  const visited = getImpersonationVisitedPaths();
  return {
    returnHref,
    blockedPaths: [...new Set([...DEFAULT_BLOCKED, ...visited])],
    endedAt: Date.now(),
    ...(notice ? { notice } : {}),
  };
}

export function dismissImpersonationExpiryNotice(): void {
  const guard = getImpersonationEndedGuard();
  if (!guard?.notice) return;
  const { notice: _notice, ...rest } = guard;
  setImpersonationEndedGuard(rest);
}

export function tenantViewFromLocation(
  pathname: string,
  searchParams: URLSearchParams,
): ImpersonationBannerState | null {
  const businessId =
    searchParams.get('businessId') ??
    pathname.match(/^\/businesses\/([^/]+)/)?.[1] ??
    pathname.match(/^\/tenants\/([^/]+)/)?.[1];
  const caseId = searchParams.get('caseId') ?? undefined;
  const onTenantSurface =
    pathname.startsWith('/tenant-detail') ||
    pathname.startsWith('/merchant-360') ||
    pathname.startsWith('/suspension') ||
    pathname.startsWith('/businesses/');
  if (!onTenantSurface || !businessId) return null;
  const decoded = decodeURIComponent(businessId);
  return {
    intensity: 'tenant-view',
    subjectName: decoded,
    subjectId: decoded,
    caseId,
    caseHref: caseId
      ? `/cases/${encodeURIComponent(caseId)}?caseId=${encodeURIComponent(caseId)}`
      : undefined,
  };
}
